import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsBillingInfoLoaded,
  IsBillingInfoLoading,
  loadBillingInfoTab,
  SelectActualClientExternalData,
  SelectClientBillingInfo,
  setIsBillingInfoLoading,
} from "../redux/reducers/clientBillingInfo.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";
import {
  IsLoadedWorkInfo,
  IsLoadingWorkInfo,
  loadWorkInfoTab,
  SelectInfoSavedClientId,
  SelectOrderedBy,
  SelectProjectsOrderedBy,
  SelectWorkOrdersOrderedBy,
  setIsLoadingWorkInfo,
} from "../redux/reducers/clientWork.reducer";
import {
  IsClientDetailInfoLoaded,
  loadClientDetails,
  SelectClientDetails,
  setIsClientDetailInfoLoading,
} from "../redux/reducers/clientDetails.reducer";

import { useClientsList } from "./useClientsList";
import { loadHeaderInfoIfNeeded } from "./clientDataLoaders";

const loadWorkInfoIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isLoadedWorkInfo,
  infoSavedClientId,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.work ||
    doneRef.current.work === clientId ||
    (isLoadedWorkInfo && infoSavedClientId && infoSavedClientId === clientId)
  ) {
    return;
  }
  inFlightRef.current.work = true;
  dispatch(setIsLoadingWorkInfo(true));
  const clientsWorkInfo = await ClientsService.getClientWorkInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsLoadingWorkInfo(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client work tab information.",
        })
      );
      dispatch(setIsLoadingWorkInfo(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.work = false;
  doneRef.current.work = clientId;
  dispatch(
    loadWorkInfoTab({
      clientId: clientId,
      data: clientsWorkInfo.data,
    })
  );
};

const loadClientDetailsIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isClientDetailInfoLoaded,
  clientDetails,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.details ||
    doneRef.current.details === clientId ||
    (isClientDetailInfoLoaded && clientDetails && clientDetails.id === clientId)
  ) {
    return;
  }
  inFlightRef.current.details = true;
  dispatch(setIsClientDetailInfoLoading(true));
  const clientsInfo = await ClientsService.getClientInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsClientDetailInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Client not found",
        })
      );
      dispatch(setIsClientDetailInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.details = false;
  doneRef.current.details = clientId;
  if (clientsInfo) dispatch(loadClientDetails(clientsInfo));
};

const loadBillingInfoIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isBillingInfoLoaded,
  clientBillingInfo,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.billing ||
    doneRef.current.billing === clientId ||
    (isBillingInfoLoaded &&
      clientBillingInfo &&
      clientBillingInfo.clientId === clientId)
  ) {
    return;
  }
  inFlightRef.current.billing = true;
  dispatch(setIsBillingInfoLoading(true));
  const clientsInfo = await ClientsService.getClientBillingInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsBillingInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Client not found",
        })
      );
      dispatch(setIsBillingInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.billing = false;
  doneRef.current.billing = clientId;
  dispatch(loadBillingInfoTab(clientsInfo));
};

export const useClientBillingInfo = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded, activeUserId } =
    useSelector(selectUserRoles);
  const isBillingInfoLoading = useSelector(IsBillingInfoLoading);
  const isBillingInfoLoaded = useSelector(IsBillingInfoLoaded);
  const actualClientExternalData = useSelector(SelectActualClientExternalData);
  const clientBillingInfo = useSelector(SelectClientBillingInfo);

  const infoSavedClientId = useSelector(SelectInfoSavedClientId);
  const isLoadingWorkInfo = useSelector(IsLoadingWorkInfo);
  const isLoadedWorkInfo = useSelector(IsLoadedWorkInfo);
  const { clientRecurringBillingsInfo: clientRecurringBillings } =
    useSelector(SelectOrderedBy);
  const { clientWorkOrdersInfo: clientWorkOrders } = useSelector(
    SelectWorkOrdersOrderedBy
  );
  const { clientProjectsInfo: clientProjects } = useSelector(
    SelectProjectsOrderedBy
  );

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const isClientDetailInfoLoaded = useSelector(IsClientDetailInfoLoaded);
  const clientDetails = useSelector(SelectClientDetails);

  // Tracks which fetch blocks are in flight, and (in doneRef) which clientId
  // each block last completed for. The callback is async and not awaited by
  // the effect, so each block's dispatch changes a dependency and re-runs the
  // effect while earlier requests are still pending. A second, overlapping
  // invocation is a *different* closure — its own `isXxxLoaded`/`clientDetails`
  // etc. reads are frozen at whatever they were when THAT closure was created,
  // so checking only those against inFlightRef isn't enough: a stale
  // invocation can still see isXxxLoaded as false (its own stale snapshot)
  // right after a newer invocation has already finished and reset the "in
  // flight" flag, and re-fire. doneRef is a plain mutable ref, so every
  // invocation (stale or not) reads its truly-current value. Also do NOT gate
  // on the redux isXxxLoading flags — they all default to true, which would
  // block the very first fetch forever.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const doneRef = useRef<Record<string, number | boolean>>({});

  const loadClientBillingInfo = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // work info
      await loadWorkInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isLoadedWorkInfo,
        infoSavedClientId,
        dispatch,
        navigate
      );
      // details info
      await loadClientDetailsIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isClientDetailInfoLoaded,
        clientDetails,
        dispatch,
        navigate
      );
      // page info
      await loadBillingInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isBillingInfoLoaded,
        clientBillingInfo,
        dispatch,
        navigate
      );
    },
    [
      dispatch,
      rolesLoaded,
      isClientsLoaded,
      isLoadedWorkInfo,
      infoSavedClientId,
      isBillingInfoLoaded,
    ]
  );

  useEffect(() => {
    loadClientBillingInfo(clientId);
  }, [loadClientBillingInfo]);

  return {
    ClientForHeaderInfo,
    activeUserId,
    clientDetails,
    isBillingInfoLoading,
    actualClientExternalData,
    clientBillingInfo,
    userRolesList,
    rolesLoaded,
    isLoadingWorkInfo,
    clientRecurringBillings,
    clientWorkOrders,
    clientProjects,
  };
};
